import apiService from './apiService.js';
import { functionsBase } from '../config/functionsBase';

const DISCORD_CONFIG = {
  GUILD_ID: '1502780741212438730',
  REQUIRED_ROLE_ID: '1456653678504316990',
  CLIENT_ID: '1458179819186422055',
  SCOPES: ['identify', 'guilds.join'],
};

const DISCORD_API_BASE = 'https://discord.com/api/v10';
const DISCORD_OAUTH_URL = 'https://discord.com/oauth2/authorize';
const VALID_INTENTS = new Set(['vip', 'account', 'admin', 'economia']);
const pendingExchanges = new Map();

/** Em netlify dev use http://localhost:8891 — paths relativos passam pelo proxy. */
const FUNCTIONS_BASE = functionsBase('/.netlify/functions');

class DiscordService {
  constructor() {
    this.config = DISCORD_CONFIG;
  }

  /** Local → localhost; produção → domínio atual. */
  getRedirectUri() {
    if (typeof window !== 'undefined' && window.location?.origin) {
      return `${window.location.origin}/auth/callback`;
    }
    return 'https://nexussmp.com.br/auth/callback';
  }

  // intent: 'vip' | 'account' | 'admin' | 'economia'
  getAuthUrl(intent = 'account') {
    const safeIntent = VALID_INTENTS.has(intent) ? intent : 'account';
    const redirectUri = this.getRedirectUri();
    // Intent vai no state do Discord — sobrevive mesmo se o storage sumir no retorno
    const state = `${safeIntent}.${this.generateNonce()}`;
    const params = new URLSearchParams({
      client_id: this.config.CLIENT_ID,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: this.config.SCOPES.join(' '),
      state,
    });

    try {
      sessionStorage.setItem('discord_oauth_state', state);
      sessionStorage.setItem('discord_oauth_intent', safeIntent);
      sessionStorage.setItem('discord_oauth_redirect', redirectUri);
      localStorage.setItem('discord_oauth_state', state);
      localStorage.setItem('discord_oauth_intent', safeIntent);
      localStorage.setItem('discord_oauth_redirect', redirectUri);
    } catch {
      // storage indisponível — o state na URL ainda carrega o intent
    }

    return `${DISCORD_OAUTH_URL}?${params.toString()}`;
  }

  generateNonce() {
    return (
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15)
    );
  }

  parseIntentFromState(state) {
    if (!state || typeof state !== 'string') return null;
    const intent = state.split('.')[0];
    return VALID_INTENTS.has(intent) ? intent : null;
  }

  getStoredRedirectUri() {
    try {
      return (
        sessionStorage.getItem('discord_oauth_redirect') ||
        localStorage.getItem('discord_oauth_redirect') ||
        this.getRedirectUri()
      );
    } catch {
      return this.getRedirectUri();
    }
  }

  /** Troca o code no servidor (evita CORS / secret no browser). */
  exchangeCodeOnServer(code) {
    const existing = pendingExchanges.get(code);
    if (existing) return existing;

    const job = this.requestDiscordExchange(code).catch((error) => {
      pendingExchanges.delete(code);
      throw error;
    });
    pendingExchanges.set(code, job);
    return job;
  }

  async requestDiscordExchange(code) {
    const redirectUri = this.getStoredRedirectUri();
    const response = await fetch(`${FUNCTIONS_BASE}/discord-exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, redirectUri }),
    });
    try {
      sessionStorage.removeItem('discord_oauth_redirect');
      localStorage.removeItem('discord_oauth_redirect');
    } catch {
      /* ignore */
    }

    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.success) {
      throw new Error(data.error || `Falha no OAuth Discord (${response.status})`);
    }
    return data;
  }

  async getUserInfo(accessToken) {
    const response = await fetch(`${DISCORD_API_BASE}/users/@me`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async verifyUserRole(userId, username) {
    const verificationResult = await apiService.verifyUser(userId, username);

    if (!verificationResult.success) {
      throw new Error(verificationResult.error);
    }

    return {
      success: true,
      user: verificationResult.user,
    };
  }

  async verifyUser(code) {
    try {
      const data = await this.exchangeCodeOnServer(code);
      const verificationResult = await this.verifyUserRole(
        data.user.id,
        data.user.username
      );

      return {
        success: true,
        user: verificationResult.user,
      };
    } catch (error) {
      console.error('Erro na verificação completa:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  async loginIdentity(code) {
    try {
      const data = await this.exchangeCodeOnServer(code);
      return {
        success: true,
        user: data.user,
        accessToken: data.accessToken || null,
      };
    } catch (error) {
      console.error('Erro no login Discord:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  getPlayerAuth() {
    try {
      const raw =
        localStorage.getItem('discordPlayerAuth') ||
        localStorage.getItem('discordVipAuth');
      if (!raw) return null;
      const data = JSON.parse(raw);
      const maxAge = 7 * 24 * 60 * 60 * 1000;
      if (!data?.user?.id || Date.now() - data.timestamp > maxAge) {
        this.clearPlayerAuth();
        return null;
      }
      return data.user;
    } catch {
      return null;
    }
  }

  /** Access token Discord do jogador (necessário para Chat VIP). */
  getPlayerAccessToken() {
    try {
      const raw =
        localStorage.getItem('discordPlayerAuth') ||
        localStorage.getItem('discordVipAuth');
      if (!raw) return null;
      const data = JSON.parse(raw);
      const maxAge = 7 * 24 * 60 * 60 * 1000;
      if (!data?.accessToken || Date.now() - data.timestamp > maxAge) {
        return null;
      }
      return data.accessToken;
    } catch {
      return null;
    }
  }

  setPlayerAuth(user, accessToken) {
    const token =
      accessToken !== undefined ? accessToken || null : this.getPlayerAccessToken();
    const payload = JSON.stringify({
      user,
      accessToken: token,
      timestamp: Date.now(),
    });
    localStorage.setItem('discordPlayerAuth', payload);
    localStorage.setItem('discordVipAuth', payload);
  }

  clearPlayerAuth() {
    localStorage.removeItem('discordPlayerAuth');
    localStorage.removeItem('discordVipAuth');
  }

  getVipAuth() {
    return this.getPlayerAuth();
  }

  setVipAuth(user) {
    this.setPlayerAuth(user);
  }

  clearVipAuth() {
    this.clearPlayerAuth();
  }

  resolveOAuthIntent(receivedState) {
    const fromState = this.parseIntentFromState(receivedState);
    let fromStorage = null;
    try {
      fromStorage =
        sessionStorage.getItem('discord_oauth_intent') ||
        localStorage.getItem('discord_oauth_intent');
    } catch {
      fromStorage = null;
    }
    try {
      sessionStorage.removeItem('discord_oauth_intent');
      localStorage.removeItem('discord_oauth_intent');
    } catch {
      /* ignore */
    }
    if (VALID_INTENTS.has(fromState)) return fromState;
    if (VALID_INTENTS.has(fromStorage)) return fromStorage;
    return 'account';
  }

  getOAuthIntent() {
    return this.resolveOAuthIntent(null);
  }

  /**
   * Valida o state. Aceita match no storage OU state no formato intent.nonce
   * (para não bloquear se o storage sumir no redirect).
   */
  validateState(receivedState) {
    if (!receivedState) return { valid: false, intent: 'account' };

    let stored = null;
    try {
      stored =
        sessionStorage.getItem('discord_oauth_state') ||
        localStorage.getItem('discord_oauth_state');
      sessionStorage.removeItem('discord_oauth_state');
      localStorage.removeItem('discord_oauth_state');
    } catch {
      stored = null;
    }

    // Compat: storage antigo guardava a query inteira
    if (stored && stored.includes('=')) {
      try {
        stored = new URLSearchParams(stored).get('state') || stored;
      } catch {
        /* ignore */
      }
    }

    const intent = this.resolveOAuthIntent(receivedState);
    const matchesStorage = Boolean(stored && stored === receivedState);
    const looksLikeOurs = Boolean(this.parseIntentFromState(receivedState));

    return {
      valid: matchesStorage || looksLikeOurs,
      intent,
    };
  }
}

const discordServiceInstance = new DiscordService();

export default discordServiceInstance;
